/*
 * Accessibility fixes (WCAG 2.1 AA)
 *
 * The theme's own templates are fixed at the source. This script covers markup
 * that is injected at runtime by installed apps (reviews, page builders, upsell
 * and popup widgets, embedded maps and videos), which cannot be edited in the
 * theme. It only adds missing accessibility information; it never removes
 * content or changes behaviour. It re-runs when apps add new markup.
 */
(function () {
  'use strict';

  var NEW_TAB_TEXT = ' (opens in a new tab)';

  var HOST_NAMES = {
    'instagram.com': 'Instagram',
    'facebook.com': 'Facebook',
    'tiktok.com': 'TikTok',
    'twitter.com': 'Twitter',
    'x.com': 'X',
    'youtube.com': 'YouTube',
    'youtu.be': 'YouTube',
    'vimeo.com': 'Vimeo',
    'linkedin.com': 'LinkedIn',
    'pinterest.com': 'Pinterest',
    'google.com/maps': 'Google Maps',
    'goo.gl/maps': 'Google Maps',
    'maps.app.goo.gl': 'Google Maps',
    'toasttab.com': 'Order online',
    'toast.site': 'Order online',
    'yelp.com': 'Yelp',
    'spotify.com': 'Spotify'
  };

  // Words found in class names / ids of icon-only buttons, mapped to a label.
  var ICON_BUTTON_NAMES = [
    [/close|dismiss/, 'Close'],
    [/next|right/, 'Next'],
    [/prev|left|back/, 'Previous'],
    [/play/, 'Play'],
    [/pause/, 'Pause'],
    [/mute/, 'Mute'],
    [/search/, 'Search'],
    [/cart|bag/, 'Cart'],
    [/menu|burger|hamburger|nav-toggle/, 'Menu'],
    [/plus|increase|increment/, 'Increase quantity'],
    [/minus|decrease|decrement/, 'Decrease quantity'],
    [/zoom/, 'Zoom image'],
    [/share/, 'Share'],
    [/wishlist|favorite|heart/, 'Add to wishlist'],
    [/account|user|login/, 'Account'],
    [/dot|bullet|pagination/, 'Go to slide']
  ];

  function text(el) {
    return (el.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function hasAccessibleName(el) {
    if ((el.getAttribute('aria-label') || '').trim()) return true;
    var labelledby = el.getAttribute('aria-labelledby');
    if (labelledby) {
      var ok = labelledby.split(/\s+/).some(function (id) {
        var ref = document.getElementById(id);
        return ref && text(ref);
      });
      if (ok) return true;
    }
    if (text(el)) return true;
    var imgs = el.querySelectorAll('img[alt]');
    for (var i = 0; i < imgs.length; i++) {
      if (imgs[i].getAttribute('alt').trim()) return true;
    }
    var svgs = el.querySelectorAll('svg[aria-label], svg title, [role="img"][aria-label]');
    for (var j = 0; j < svgs.length; j++) {
      if ((svgs[j].getAttribute('aria-label') || text(svgs[j])).trim()) return true;
    }
    if ((el.getAttribute('title') || '').trim()) return true;
    return false;
  }

  function nameFromHref(href) {
    if (!href) return '';
    var lower = href.toLowerCase();
    for (var host in HOST_NAMES) {
      if (lower.indexOf(host) > -1) return HOST_NAMES[host];
    }
    if (lower.indexOf('mailto:') === 0) return 'Email ' + href.slice(7).split('?')[0];
    if (lower.indexOf('tel:') === 0) return 'Call ' + href.slice(4);
    try {
      var url = new URL(href, window.location.href);
      if (url.host === window.location.host) {
        var parts = url.pathname.split('/').filter(Boolean);
        if (!parts.length) return 'Home';
        var last = decodeURIComponent(parts[parts.length - 1]).replace(/[-_]+/g, ' ');
        return last.charAt(0).toUpperCase() + last.slice(1);
      }
      return url.host.replace(/^www\./, '');
    } catch (e) {
      return '';
    }
  }

  function nameFromClasses(el) {
    var hint = ((el.className && el.className.baseVal !== undefined ? el.className.baseVal : el.className) || '') +
      ' ' + (el.id || '') + ' ' + (el.getAttribute('data-action') || '') + ' ' + (el.getAttribute('name') || '');
    hint = hint.toLowerCase();
    for (var i = 0; i < ICON_BUTTON_NAMES.length; i++) {
      if (ICON_BUTTON_NAMES[i][0].test(hint)) return ICON_BUTTON_NAMES[i][1];
    }
    return '';
  }

  function nameFromFilename(src) {
    if (!src) return '';
    var file = src.split('?')[0].split('/').pop() || '';
    file = decodeURIComponent(file)
      .replace(/\.[a-z0-9]+$/i, '')
      .replace(/_[0-9a-f]{8}-[0-9a-f-]{27,}$/i, '')
      .replace(/_\d+x\d*$/i, '')
      .replace(/[-_]+/g, ' ')
      .replace(/\b(img|image|copy|final|v\d+|\d{3,})\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
    return file.length > 2 ? file.charAt(0).toUpperCase() + file.slice(1) : '';
  }

  function isHidden(el) {
    return !!el.closest('[aria-hidden="true"]');
  }

  // 1.1.1 Non-text Content: every image has an alt attribute.
  function fixImages(root) {
    root.querySelectorAll('img:not([alt])').forEach(function (img) {
      var w = img.getAttribute('width');
      var h = img.getAttribute('height');
      var tiny = (w && parseInt(w, 10) <= 2) || (h && parseInt(h, 10) <= 2);
      var control = img.closest('a, button');
      if (tiny || isHidden(img) || (control && text(control))) {
        img.setAttribute('alt', '');
        return;
      }
      var name = img.getAttribute('title') || img.getAttribute('data-alt') || nameFromFilename(img.currentSrc || img.src || img.getAttribute('data-src'));
      img.setAttribute('alt', name || '');
    });
    // Decorative inline SVG icons inside controls that already have text.
    root.querySelectorAll('a svg:not([aria-hidden]), button svg:not([aria-hidden])').forEach(function (svg) {
      var control = svg.closest('a, button');
      if (control && (text(control) || (control.getAttribute('aria-label') || '').trim())) {
        if (!svg.getAttribute('aria-label') && !svg.querySelector('title')) {
          svg.setAttribute('aria-hidden', 'true');
          svg.setAttribute('focusable', 'false');
        }
      }
    });
  }

  // 2.4.4 Link Purpose / 4.1.2 Name, Role, Value: links and buttons have names.
  function fixControls(root) {
    root.querySelectorAll('a[href]').forEach(function (a) {
      if (isHidden(a) || hasAccessibleName(a)) return;
      var name = nameFromClasses(a) || nameFromHref(a.getAttribute('href'));
      if (name) a.setAttribute('aria-label', name);
    });

    root.querySelectorAll('button, [role="button"], input[type="button"], input[type="submit"]').forEach(function (b) {
      if (isHidden(b)) return;
      if (b.tagName === 'INPUT') {
        if (!(b.value || '').trim() && !(b.getAttribute('aria-label') || '').trim()) {
          b.setAttribute('aria-label', b.type === 'submit' ? 'Submit' : (nameFromClasses(b) || 'Button'));
        }
        return;
      }
      if (hasAccessibleName(b)) return;
      var name = nameFromClasses(b);
      if (!name) {
        var svg = b.querySelector('svg');
        if (svg) name = nameFromClasses(svg);
      }
      if (name) b.setAttribute('aria-label', name);
    });

    // Non-button elements given role="button" must be reachable and operable by keyboard.
    root.querySelectorAll('[role="button"]:not(button):not(a[href]):not([tabindex])').forEach(function (el) {
      el.setAttribute('tabindex', '0');
      el.setAttribute('data-a11y-key', '');
    });
  }

  // 3.2.5 / G201: warn screen reader users before a link opens a new tab.
  function fixNewTabLinks(root) {
    root.querySelectorAll('a[target="_blank"]:not([data-a11y-newtab])').forEach(function (a) {
      a.setAttribute('data-a11y-newtab', '');
      if (!a.getAttribute('rel')) a.setAttribute('rel', 'noopener');
      var label = a.getAttribute('aria-label');
      if (label) {
        if (label.indexOf('new tab') === -1) a.setAttribute('aria-label', label + NEW_TAB_TEXT);
        return;
      }
      var hint = document.createElement('span');
      hint.className = 'a11y-visually-hidden';
      hint.textContent = NEW_TAB_TEXT;
      a.appendChild(hint);
    });
  }

  // 4.1.2: embedded frames (maps, videos, forms) have a title.
  function fixFrames(root) {
    root.querySelectorAll('iframe:not([title])').forEach(function (frame) {
      var src = (frame.getAttribute('src') || frame.getAttribute('data-src') || '').toLowerCase();
      var title = 'Embedded content';
      if (src.indexOf('youtube') > -1 || src.indexOf('youtu.be') > -1) title = 'YouTube video';
      else if (src.indexOf('vimeo') > -1) title = 'Vimeo video';
      else if (src.indexOf('google.com/maps') > -1 || src.indexOf('maps.google') > -1) title = 'Map of Pax & Beneficia location';
      else if (src.indexOf('instagram') > -1) title = 'Instagram post';
      else if (src.indexOf('klaviyo') > -1) title = 'Email sign-up form';
      else if (src.indexOf('googletagmanager') > -1) { frame.setAttribute('aria-hidden', 'true'); frame.setAttribute('tabindex', '-1'); title = 'Analytics'; }
      else if (src.indexOf('recaptcha') > -1) title = 'reCAPTCHA';
      frame.setAttribute('title', title);
    });
  }

  // 1.3.1 / 3.3.2 / 4.1.2: form fields have a label.
  function fixFormFields(root) {
    root.querySelectorAll('input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="image"]), select, textarea').forEach(function (field) {
      if (isHidden(field)) return;
      if ((field.getAttribute('aria-label') || '').trim() || field.getAttribute('aria-labelledby')) return;
      if (field.id) {
        var lbl = document.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(field.id) : field.id) + '"]');
        if (lbl && text(lbl)) return;
      }
      var wrapping = field.closest('label');
      if (wrapping && text(wrapping)) return;
      var name = field.getAttribute('placeholder') || field.getAttribute('title');
      if (!name) {
        var n = (field.getAttribute('name') || field.getAttribute('type') || '').toLowerCase();
        if (n.indexOf('email') > -1) name = 'Email address';
        else if (n.indexOf('phone') > -1 || n === 'tel') name = 'Phone number';
        else if (n.indexOf('quantity') > -1) name = 'Quantity';
        else if (n === 'q' || n === 'search') name = 'Search';
        else if (field.tagName === 'SELECT') name = 'Select an option';
      }
      if (name) field.setAttribute('aria-label', name.trim());
    });
  }

  // 4.1.2: focusable elements must not sit inside content hidden from screen readers.
  function fixHiddenFocusables(root) {
    root.querySelectorAll('[aria-hidden="true"] a[href], [aria-hidden="true"] button, [aria-hidden="true"] input, [aria-hidden="true"] select, [aria-hidden="true"] textarea, [aria-hidden="true"] [tabindex]:not([tabindex="-1"])').forEach(function (el) {
      if (!el.hasAttribute('data-a11y-tabindex')) {
        el.setAttribute('data-a11y-tabindex', el.getAttribute('tabindex') || '');
      }
      el.setAttribute('tabindex', '-1');
    });
    // Restore keyboard access once the container is shown again.
    root.querySelectorAll('[data-a11y-tabindex]').forEach(function (el) {
      if (isHidden(el)) return;
      var original = el.getAttribute('data-a11y-tabindex');
      if (original) el.setAttribute('tabindex', original);
      else el.removeAttribute('tabindex');
      el.removeAttribute('data-a11y-tabindex');
    });
  }

  // 1.3.1: headings with no text are not announced as empty headings.
  function fixEmptyHeadings(root) {
    root.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(function (h) {
      if (!text(h) && !h.querySelector('img[alt]:not([alt=""])')) {
        h.setAttribute('role', 'presentation');
      }
    });
  }

  // 4.1.1: invalid "aria-role" attribute used by some components (it never had any effect).
  function fixInvalidAria(root) {
    root.querySelectorAll('[aria-role]').forEach(function (el) {
      el.removeAttribute('aria-role');
    });
  }

  function run(root) {
    root = root || document;
    try {
      fixInvalidAria(root);
      fixImages(root);
      fixControls(root);
      fixNewTabLinks(root);
      fixFrames(root);
      fixFormFields(root);
      fixHiddenFocusables(root);
      fixEmptyHeadings(root);
    } catch (e) {
      if (window.console) console.warn('a11y-fixes', e);
    }
  }

  // Keyboard support for click-only controls: elements this script made focusable,
  // the back-to-top control, and the mobile menu "Back" links. Components that
  // already handle the keyboard themselves (e.g. content toggles) are left alone.
  var CLICK_ONLY = '[data-a11y-key], #go-top, .submenu-back > a[role="button"]';
  document.addEventListener('keydown', function (e) {
    var el = e.target;
    if (!el || !el.matches || e.defaultPrevented) return;
    if (!el.matches(CLICK_ONLY)) return;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      el.click();
    }
  });

  var pending = false;
  function schedule() {
    if (pending) return;
    pending = true;
    window.setTimeout(function () {
      pending = false;
      run(document);
    }, 300);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { run(document); });
  } else {
    run(document);
  }
  window.addEventListener('load', function () { run(document); });

  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].type === 'attributes' || (mutations[i].addedNodes && mutations[i].addedNodes.length)) {
          schedule();
          return;
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden'] });
  }
})();
